/**
 * The four shared-chrome components, driven entirely by the screen contract.
 *
 * Story #5343 (Launch W1). The 2026-09-02 design-match audit scored all 143
 * approved mockups against the shipped code: 260 of 954 contracted components
 * present (27%). 317 of the 694 missing were these four, absent from nearly
 * every screen:
 *
 *   ScreenHeader  breadcrumb + title + description   (G02)
 *   DomainTabs    the per-domain tab strip
 *   FocusBar      Focus / Resolved owner / Scope evidence + capability badge
 *   ScreenFooter  the two footer callouts
 *
 * They are mounted ONCE, by Layout.tsx, and read the current route's contract
 * row. No page component is edited, which is the point: 317 missing components
 * across 143 screens is one build, not 143 edits.
 *
 * Layout matches the approved mockups (verified against E04_environments_
 * overview.png and M02_my_account_security.png on 2026-09-02):
 *
 *   Environments -> select immutable row -> Overview        <- ScreenHeader
 *   Overview                                                   (breadcrumb,
 *   Explain environment identity, purpose, ownership...         title, purpose)
 *   Fleet  Compare  [Overview]  Services  Data  ...         <- DomainTabs
 *   +-----------------------------------------------+
 *   | Focus        | Resolved owner | Scope evidence |       <- FocusBar
 *   +-----------------------------------------------+
 *   ...page content...
 *   +------------------+  +------------------+
 *   | what this screen |  | the boundary     |               <- ScreenFooter
 *   +------------------+  +------------------+
 */
import React, { useState } from 'react';
import { NavLink, useLocation, matchPath } from 'react-router-dom';
import { Check, HelpCircle, Link2 } from 'lucide-react';
import { useScreenContract, siblingScreens } from './useScreenContract';
import { parseCapability, tierBadgeClasses } from './capability';
import type { ScreenContract } from './screenContract.generated';

/**
 * Substitute the current URL's route params into a sibling screen's pattern.
 *
 * On /environments/env-prod/overview, sibling E05's pattern
 * '/environments/:environmentId/services' must become
 * '/environments/env-prod/services' -- otherwise every tab on every
 * entity-scoped screen links to a literal ':environmentId' and 404s.
 * Returns null when a parameter cannot be filled, so the tab renders as
 * inert text rather than a broken link.
 */
export function fillRouteParams(
  pattern: string,
  params: Readonly<Record<string, string | undefined>>,
): string | null {
  const segments = pattern.split('/');
  const filled: string[] = [];
  for (const segment of segments) {
    if (!segment.startsWith(':')) {
      filled.push(segment);
      continue;
    }
    const value = params[segment.slice(1)];
    if (!value) return null;
    filled.push(value);
  }
  return filled.join('/');
}

/** The route params of the CURRENT screen, for filling sibling patterns. */
function useCurrentParams(screen: ScreenContract | null): Record<string, string | undefined> {
  const { pathname } = useLocation();
  if (!screen) return {};
  const match = matchPath({ path: screen.routePattern, end: true }, pathname);
  return match?.params ?? {};
}

/** Where a sibling tab points, or null when it cannot be linked from here. */
function tabHref(
  sibling: ScreenContract,
  params: Record<string, string | undefined>,
): string | null {
  const path = fillRouteParams(sibling.routePattern, params);
  if (!path) return null;
  return sibling.routeQuery ? `${path}?${sibling.routeQuery}` : path;
}

/* ------------------------------------------------------------------ */
/* G02 -- breadcrumb, title, description                               */
/* ------------------------------------------------------------------ */

/**
 * The breadcrumb line, page title and purpose.
 *
 * The breadcrumb text is the contract's "Navigation position" cell verbatim
 * -- the mockups render exactly that string, e.g. "Environments -> select
 * immutable row -> Overview". It is prose describing the path taken, not a
 * series of links, so it is rendered as text with the screen's immutable id
 * beside it. "Copy link" satisfies G02's copy-canonical-link contract item.
 */
export const ScreenHeader: React.FC<{ screen: ScreenContract }> = ({ screen }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is unavailable (insecure context, denied permission). Say
      // nothing rather than claim a copy that did not happen.
    }
  };

  return (
    <div className="mb-4" data-testid="screen-header" data-screen-id={screen.id}>
      <div className="flex items-start gap-3">
        <p className="flex-1 text-sm text-slate-500">
          {screen.navPosition || `${screen.lhs} / ${screen.tab}`}
        </p>
        <span
          title="Immutable screen id from the approved design"
          className="rounded-full border border-iflow-900/10 bg-white px-2 py-0.5 text-[10px] font-semibold tracking-wider text-slate-500"
        >
          {screen.id}
        </span>
        <button
          type="button"
          onClick={copy}
          title="Copy this screen's canonical link"
          className="inline-flex items-center gap-1 rounded-md border border-iflow-900/15 px-2 py-0.5 text-[11px] font-medium text-iflow-800 hover:bg-iflow-50"
        >
          {copied ? <Check className="h-3 w-3" /> : <Link2 className="h-3 w-3" />}
          {copied ? 'Copied' : 'Copy link'}
        </button>
      </div>
      <h1 className="mt-1 text-2xl font-bold tracking-tight text-iflow-900">{screen.tab}</h1>
      {screen.purpose && <p className="mt-1 text-sm text-slate-500">{screen.purpose}</p>}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* The per-domain tab strip                                            */
/* ------------------------------------------------------------------ */

const TAB_BASE =
  '-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors';

/**
 * One tab per screen sharing this screen's domain AND group, in workbook
 * order -- so Administration -> Identity shows its six screens, and
 * Environments shows all eighteen.
 *
 * Underline idiom, matching the mockups. The four hand-written domain navs
 * (CustomersNav, KnowledgeNav, AdministrationNav, OperationsNav) used a pill
 * idiom instead and between them covered 19 of 143 screens; they now stand
 * down via ShellChromeContext rather than being deleted, so nothing breaks if
 * a route has no contract row.
 */
export const DomainTabs: React.FC<{ screen: ScreenContract }> = ({ screen }) => {
  const params = useCurrentParams(screen);
  const siblings = siblingScreens(screen);
  if (siblings.length < 2) return null;

  return (
    <nav
      aria-label={`${screen.lhs} sections`}
      data-testid="domain-tabs"
      className="mb-4 flex flex-wrap gap-1 overflow-x-auto border-b border-iflow-900/10"
    >
      {siblings.map((sibling) => {
        const href = tabHref(sibling, params);
        const isCurrent = sibling.id === screen.id;

        // A sibling we cannot build a URL for (its pattern needs a param this
        // route does not carry) is shown but not linked -- the design's tab
        // strip is a map of the domain, so hiding it would misrepresent it.
        if (!href) {
          return (
            <span
              key={sibling.id}
              title={`Open a specific ${screen.lhs.toLowerCase()} record to reach ${sibling.tab}`}
              className={`${TAB_BASE} cursor-default border-transparent text-slate-300`}
            >
              {sibling.tab}
            </span>
          );
        }

        return (
          <NavLink
            key={sibling.id}
            to={href}
            end
            aria-current={isCurrent ? 'page' : undefined}
            className={`${TAB_BASE} ${
              isCurrent
                ? 'border-iflow-600 text-iflow-900'
                : 'border-transparent text-slate-500 hover:border-iflow-900/20 hover:text-iflow-800'
            }`}
          >
            {sibling.tab}
          </NavLink>
        );
      })}
    </nav>
  );
};

/* ------------------------------------------------------------------ */
/* The Focus / Owner / Scope-evidence bar                              */
/* ------------------------------------------------------------------ */

const FIELD_LABEL =
  'block text-[10px] font-semibold uppercase tracking-wider text-slate-400';

/**
 * What this screen is scoped to, who owns the data, and what it may do.
 *
 * The capability badge is colour-coded by the highest risk tier the contract
 * names: grey R0, sky R1, amber R2, red R3. When the mode cell cannot be
 * parsed the raw contract text is shown with a neutral badge -- never a
 * guessed tier, because a screen wrongly labelled "Read-only" invites an
 * operator to click something consequential.
 */
export const FocusBar: React.FC<{
  screen: ScreenContract;
  onOpenHelp?: () => void;
}> = ({ screen, onOpenHelp }) => {
  const capability = parseCapability(screen.mode);

  return (
    <div
      data-testid="focus-bar"
      className="mb-4 flex flex-wrap items-stretch gap-x-6 gap-y-3 rounded-xl border border-iflow-900/10 bg-white px-4 py-3"
    >
      <div className="min-w-[12rem] flex-1">
        <span className={FIELD_LABEL}>Focus</span>
        <p className="mt-0.5 text-sm text-iflow-900" title={screen.focus}>
          {screen.focus || 'Not stated in the contract'}
        </p>
      </div>

      <div className="min-w-[12rem] flex-1 border-l border-iflow-900/10 pl-6">
        <span className={FIELD_LABEL}>Resolved owner</span>
        <p className="mt-0.5 text-sm text-iflow-900" title={screen.owner}>
          {screen.owner || 'Not stated in the contract'}
        </p>
      </div>

      <div className="min-w-[12rem] flex-1 border-l border-iflow-900/10 pl-6">
        <span className={FIELD_LABEL}>Scope evidence</span>
        <p className="mt-0.5 text-sm text-iflow-900" title={screen.evidence}>
          {screen.evidence || 'No evidence identifiers recorded'}
        </p>
      </div>

      <div className="flex items-center gap-2 border-l border-iflow-900/10 pl-6">
        {onOpenHelp && (
          <button
            type="button"
            onClick={onOpenHelp}
            title={screen.helpId ? `Contextual help (${screen.helpId})` : 'Contextual help'}
            className="inline-flex items-center gap-1 rounded-lg border border-iflow-900/15 px-2.5 py-1.5 text-xs font-medium text-iflow-800 hover:bg-iflow-50"
          >
            <HelpCircle className="h-3.5 w-3.5" />
            Help
          </button>
        )}
        <span
          title={capability.raw}
          data-testid="capability-badge"
          data-tier={capability.highest ?? 'unparsed'}
          className={`max-w-[16rem] truncate rounded-full border px-2.5 py-1 text-xs font-semibold ${tierBadgeClasses(capability.highest)}`}
        >
          {capability.label ?? capability.raw ?? 'Capability not stated'}
        </span>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* The footer callouts                                                 */
/* ------------------------------------------------------------------ */

/**
 * Two callouts: what this screen is, and where its boundary lies.
 *
 * HONESTY NOTE. The mockups carry bespoke per-screen copy ("Summary, not a
 * second owner", "Passwords are never retrievable"). That wording exists ONLY
 * inside the 143 mockup PNGs -- a search of every sheet of the launch workbook
 * on 2026-09-02 found none of it. Rather than invent 286 sentences, these
 * callouts are composed from contract fields the workbook does carry: the
 * canonical owner, the parsed capability tier, and the primary control. The
 * slots and their meaning match the design; the exact sentences do not, and
 * will not until the callout copy is authored into the workbook.
 */
export const ScreenFooter: React.FC<{ screen: ScreenContract }> = ({ screen }) => {
  const capability = parseCapability(screen.mode);
  const dangerous = capability.highest === 'R3';

  return (
    <div
      data-testid="screen-footer"
      className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2"
    >
      <div className="rounded-xl border border-sky-200 bg-sky-50 p-4">
        <p className="text-sm font-semibold text-sky-900">
          {screen.owner ? 'Canonical owner' : 'This screen'}
        </p>
        <p className="mt-1 text-sm text-sky-800">
          {screen.owner
            ? `${screen.owner} owns this data. This screen shows it; it is never a second store.`
            : screen.purpose}
        </p>
      </div>

      <div
        className={`rounded-xl border p-4 ${
          dangerous
            ? 'border-red-300 bg-red-50'
            : capability.highest === 'R2'
              ? 'border-amber-300 bg-amber-50'
              : 'border-slate-200 bg-slate-50'
        }`}
      >
        <p
          className={`text-sm font-semibold ${
            dangerous ? 'text-red-900' : capability.highest === 'R2' ? 'text-amber-900' : 'text-slate-700'
          }`}
        >
          {capability.label ?? 'Capability not stated'}
        </p>
        <p
          className={`mt-1 text-sm ${
            dangerous ? 'text-red-800' : capability.highest === 'R2' ? 'text-amber-800' : 'text-slate-600'
          }`}
        >
          {capability.summary ?? screen.mode}
          {screen.primaryControl ? ` ${screen.primaryControl}` : ''}
        </p>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* The shell's mount points                                            */
/* ------------------------------------------------------------------ */

/** Everything the shell draws ABOVE the page: header, tabs, focus bar. */
export const ScreenChromeTop: React.FC<{ onOpenHelp?: () => void }> = ({ onOpenHelp }) => {
  const screen = useScreenContract();
  if (!screen) return null;
  return (
    <>
      <ScreenHeader screen={screen} />
      <DomainTabs screen={screen} />
      <FocusBar screen={screen} onOpenHelp={onOpenHelp} />
    </>
  );
};

/** Everything the shell draws BELOW the page: the footer callouts. */
export const ScreenChromeBottom: React.FC = () => {
  const screen = useScreenContract();
  if (!screen) return null;
  return <ScreenFooter screen={screen} />;
};
